import type { Path } from 'slate'
import { Point, Range } from 'slate'
import { useSlateSelection, useSlateStatic } from 'slate-react'
import { Editor } from '@tunasong/plugin-lib'
import type { TunaEditor } from '@tunasong/plugin-lib'

/**
 * Pure helper: is the block at `path` strictly inside the selection range?
 *
 * "Strictly inside" means the selection extends past the block on BOTH sides — the
 * selection's start is strictly before the block's start, AND the selection's end is
 * strictly after the block's end. Endpoints that touch (e.g., selection ending exactly at
 * the block's start) do not count.
 *
 * Why strict and not "fully contained": Slate normalizes the end of an in-text selection
 * to the start of the next block. A trailing empty block has a collapsed range AT that
 * exact point, so a non-strict "fully contained" check would mark it as in-range even
 * though the user only selected the line above. Strict interior semantics avoid that.
 *
 * Returns false for:
 * - null or collapsed selections
 * - paths that don't resolve to a node in the editor
 * - the start and end blocks of the selection (let native ::selection paint their partial
 *   or full coverage; the block bar is reserved for interior blocks)
 */
export const isInsideSelectionRange = (
  editor: TunaEditor,
  selection: Range | null,
  path: Path
): boolean => {
  if (!selection) {
    return false
  }

  // Why a try/catch around the whole body: this runs from CoreBlock on every selection
  // change. During Slate normalization races the `selection` from `useSlateSelection`
  // can transiently hold a `Range`-shaped object whose endpoint `path` is `undefined`
  // (GH #1204 / Sentry TUNASONG-VK), and `Editor.range(editor, path)` can also fail
  // when called during teardown. Any throw propagates to the editor's React error
  // boundary, replacing the whole document with an error screen. The pure helper
  // should silently treat all of these as "not in range" and let the next selection
  // settle the picture.
  try {
    if (Range.isCollapsed(selection)) {
      return false
    }

    const blockRange = Editor.range(editor, path)
    const [bs, be] = Range.edges(blockRange)
    const [ss, se] = Range.edges(selection)

    return Point.compare(ss, bs) < 0 && Point.compare(se, be) > 0
  } catch {
    return false
  }
}

/**
 * Returns true when the block at `path` is strictly inside the current selection range.
 *
 * Why this exists: native ::selection paints text glyphs only. Empty lines (no glyphs) and
 * inline void elements (e.g., chords) within multi-block selections become invisible or
 * fragment into per-element rectangles. Consumers can use this hook to apply a contiguous
 * block-level selection background, restoring visibility of system status (Nielsen #1) and
 * Gestalt continuity across multi-block selections.
 *
 * The start and end blocks of an expanded selection return false — let native ::selection
 * handle them. A trailing empty block adjacent to a single-line selection also returns
 * false; only blocks the selection extends strictly past on both sides are marked.
 */
export const useIsInSelectionRange = (path: Path): boolean => {
  // Why useSlateStatic over useSlate: useSlate subscribes the consumer to every
  // editor mutation (each keystroke, normalization op, etc.) and re-renders on
  // each one. CoreBlock calls this hook for every block in the document, so
  // useSlate would force React to reconcile N CoreBlock subtrees on every
  // keystroke — visible typing latency on long songs. useSlateStatic returns
  // the editor handle without subscribing; useSlateSelection below correctly
  // scopes re-renders to selection changes, which is when this hook's output
  // can actually change.
  const editor = useSlateStatic() as unknown as TunaEditor
  const selection = useSlateSelection()
  return isInsideSelectionRange(editor, selection, path)
}
